import { useState } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { Check } from 'lucide-react';
import { ChipOption } from '@/types/assessment';

interface SelectableChipProps {
  option: ChipOption;
  selected: boolean;
  disabled: boolean;
  expandable?: boolean;
  onToggle: (id: string) => void;
}

const SelectableChip = ({ option, selected, disabled, expandable = false, onToggle }: SelectableChipProps) => {
  const [hovered, setHovered] = useState(false);
  const prefersReducedMotion = useReducedMotion();

  const hasDetails = expandable && (!!option.description || !!option.looksLike);
  const showDetails = hasDetails && (hovered || selected);

  return (
    <div
      className="flex flex-col"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <motion.div
        whileHover={!disabled && !prefersReducedMotion ? { scale: 1.01, y: -1 } : {}}
        whileTap={!disabled && !prefersReducedMotion ? { scale: 0.99 } : {}}
        className={`relative rounded-2xl border-2 text-sm font-medium transition-all duration-200 ${
          selected
            ? 'border-primary bg-primary/8 shadow-[0_0_0_1px_hsl(var(--primary)/0.12),0_4px_12px_hsl(var(--primary)/0.1)]'
            : disabled
            ? 'border-border/30 bg-muted/30 opacity-50 cursor-not-allowed'
            : 'border-border bg-card hover:border-primary/40 hover:shadow-[0_2px_8px_hsl(var(--primary)/0.06)] shadow-[0_1px_2px_hsl(20_18%_15%/0.03)]'
        }`}
      >
        <button
          type="button"
          onClick={() => !disabled && onToggle(option.id)}
          aria-pressed={selected}
          disabled={disabled}
          className="w-full flex items-center gap-3 px-5 py-4 text-left cursor-pointer select-none min-w-0 disabled:cursor-not-allowed"
        >
          {/* Selection indicator */}
          <div className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center shrink-0 transition-all duration-200 ${
            selected
              ? 'bg-primary border-primary'
              : 'border-border/80 bg-background'
          }`}>
            <AnimatePresence>
              {selected && (
                <motion.div
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0, opacity: 0 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 15 }}
                >
                  <Check className="w-3.5 h-3.5 text-primary-foreground" strokeWidth={3} />
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Label */}
          <span className={`flex-1 font-semibold transition-colors duration-200 ${
            selected ? 'text-primary' : disabled ? 'text-muted-foreground' : 'text-foreground'
          }`}>
            {option.label}
          </span>

          {/* Subtle arrow on hover */}
          {hasDetails && !selected && (
            <motion.span
              initial={{ opacity: 0, x: -4 }}
              animate={{ opacity: hovered ? 0.4 : 0, x: hovered ? 0 : -4 }}
              className="text-muted-foreground text-xs shrink-0"
            >
              ›
            </motion.span>
          )}
        </button>
      </motion.div>

      {/* Description + "Looks like" — shows on hover AND stays when selected */}
      <AnimatePresence>
        {showDetails && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: [0.25, 0.1, 0.25, 1] }}
            className="overflow-hidden"
          >
            <div className={`mt-1.5 mx-2 px-5 py-4 rounded-xl text-sm transition-colors duration-200 ${
              selected
                ? 'bg-primary/6 border border-primary/10'
                : 'bg-secondary/60 border border-border/40'
            }`}>
              {option.description && (
                <p className="text-muted-foreground leading-relaxed">{option.description}</p>
              )}
              {option.looksLike && (
                <p className="text-foreground/80 mt-2 leading-relaxed">
                  <span className="text-primary font-semibold text-xs uppercase tracking-wider">Looks like: </span>
                  {option.looksLike}
                </p>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SelectableChip;
